import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, Users, Save, Loader2, Upload, ChevronDown, Check, Sparkles, X } from 'lucide-react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { useAgents, useAdminTools, useCreateTeam, useModels, useTeams, useTools, useUpdateTeam } from '../../hooks';
import teamService from '../../services/teamService';
import agentService from '../../services/agentService';
import Toast from '../Toast';
import DiscoverTeamSection from './DiscoverTeamSection';
import { cn } from '../../lib/utils';

const accentColors = ['#721b1f', '#6D91B9', '#3EA49D', '#222e44', '#419400', '#d74120'];

const patterns = [
  { id: 'sequential', label: 'Sequential', desc: 'Agents run one after another in a fixed order.' },
  { id: 'parallel', label: 'Parallel', desc: 'Agents run concurrently and results merge.' },
  { id: 'hierarchical', label: 'Hierarchical', desc: 'A lead agent delegates to specialist agents.' },
  { id: 'debate', label: 'Debate', desc: 'Agents argue perspectives; a judge resolves.' },
  { id: 'round_robin', label: 'Round Robin', desc: 'Agents take turns refining shared output.' },
];

const inputClass =
  'w-full bg-paper border border-shell-border rounded-lg px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40';

// S3 serves uploads as binary/octet-stream; browsers refuse to render SVGs
// without the correct MIME type, so icons are always converted to PNG.
function svgToPngDataUrl(svgMarkup, size = 128) {
  return new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(new Blob([svgMarkup], { type: 'image/svg+xml' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      canvas.getContext('2d').drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(blobUrl);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = err => {
      URL.revokeObjectURL(blobUrl);
      reject(err);
    };
    img.src = blobUrl;
  });
}

/** Predefined team glyph (two figures) on a tinted tile, as a PNG data URL. */
function createTeamIconDataUrl(color) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="14" fill="${color}" fill-opacity="0.15"/>
      <circle cx="25" cy="26" r="6" fill="none" stroke="${color}" stroke-width="3"/>
      <path d="M14 48c0-6 5-10 11-10s11 4 11 10" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
      <circle cx="42" cy="24" r="5" fill="none" stroke="${color}" stroke-width="3"/>
      <path d="M40 37c6 0 10 4 10 9" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
    </svg>`;
  return svgToPngDataUrl(svg.trim());
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    // Legacy SVG uploads are served by S3 as binary/octet-stream and won't render in <img>.
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:')) return icon;
  return `data:image/png;base64,${icon}`;
}

function patternToFlags(pattern) {
  return {
    respond_directly: pattern === 'sequential' || pattern === 'round_robin',
    determine_input_for_members: pattern === 'hierarchical' || pattern === 'debate',
    delegate_ask_to_all_members: pattern === 'parallel',
  };
}

function flagsToPattern(detail) {
  if (detail?.delegate_ask_to_all_members || detail?.delegate_task_to_all_members) return 'parallel';
  if (detail?.determine_input_for_members) return 'hierarchical';
  return 'sequential';
}

function getTeamAccent(team, index) {
  if (team?.icon_color && accentColors.includes(team.icon_color)) return team.icon_color;
  return accentColors[index % accentColors.length];
}

function parseModelProviders(modelsData) {
  const raw = modelsData?.models;
  if (!Array.isArray(raw)) return [];
  return raw.map(providerObj => {
    const providerName = Object.keys(providerObj)[0];
    const models = providerObj[providerName] || [];
    return {
      id: providerName,
      label: providerName,
      models: models.map(m => ({ id: m.id, label: m.name || m.id })),
    };
  });
}

function findProviderForModel(providers, modelId) {
  const provider = providers.find(p => p.models.some(m => m.id === modelId));
  return provider?.id || '';
}

function SimpleSelect({ label, value, options, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const current = options.find(o => o.id === value);
  return (
    <div className="relative">
      <label className="text-sm text-ink mb-1.5 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-paper border border-shell-border rounded-lg px-3 py-2.5 text-sm text-ink hover:border-ink/20">
        <span className={current ? '' : 'text-mute'}>
          {current ? current.label : placeholder || `Select ${label.toLowerCase()}`}
        </span>
        <ChevronDown className="w-4 h-4 text-mute" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-paper-2 border border-shell-border rounded-lg py-1 max-h-56 overflow-y-auto">
            {options.length === 0 && <p className="px-3 py-2 text-xs text-mute">None available</p>}
            {options.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink hover:bg-paper">
                <span>{o.label}</span>
                {o.id === value && <Check className="w-3.5 h-3.5 text-primary" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MultiSelect({ label, value, onToggle, options }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <label className="text-sm text-ink mb-1.5 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-paper border border-shell-border rounded-lg px-3 py-2.5 text-sm text-ink hover:border-ink/20">
        <span className={value.length ? '' : 'text-mute'}>
          {value.length ? `${value.length} selected` : 'Select to add'}
        </span>
        <ChevronDown className="w-4 h-4 text-mute" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-paper-2 border border-shell-border rounded-lg py-1 max-h-56 overflow-y-auto">
            {options.length === 0 && <p className="px-3 py-2 text-xs text-mute">None available</p>}
            {options.map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onToggle(opt.id)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink hover:bg-paper">
                <span className="flex items-center gap-2">
                  {opt.label}
                  {opt.tag && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{opt.tag}</span>
                  )}
                </span>
                {value.includes(opt.id) && <Check className="w-3.5 h-3.5 text-primary" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Teams builder — matches labos/Team.jsx reference (list + editor).
 * APIs unchanged:
 *  - list   → useTeams → teamService.getAllTeam
 *  - detail → teamService.getTeamDetails
 *  - create → useCreateTeam → teamService.createTeam
 *  - update → useUpdateTeam → teamService.updateTeam
 */
const TeamSection = () => {
  const { user } = useContextHook(AuthContext, v => ({ user: v.user }));
  const teamsQuery = useTeams(user?.data?.ID);
  const agentsQuery = useAgents();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();

  const teams = teamsQuery?.data?.teams || [];
  const agents = useMemo(
    () =>
      (agentsQuery?.data?.agents || []).map(a => ({
        id: a.id,
        name: a.agent_name,
        description: a.description,
        icon: a.icon,
        icon_color: accentColors[(a.agent_name?.charCodeAt(0) || 0) % accentColors.length],
      })),
    [agentsQuery?.data?.agents],
  );

  const [listTab, setListTab] = useState('mine'); // 'mine' | 'discover'
  const [activeTeam, setActiveTeam] = useState(null); // 'new' | team object | null
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [query, setQuery] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconColor, setIconColor] = useState(accentColors[0]);
  const [iconDataUrl, setIconDataUrl] = useState(null); // uploaded/existing image (data URL or http URL)
  const fileInputRef = useRef(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [memberRoles, setMemberRoles] = useState({}); // agent_id -> assigned task/role
  const [pattern, setPattern] = useState('sequential');
  const [rules, setRules] = useState('');
  const [provider, setProvider] = useState('');
  const [modelId, setModelId] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);

  const { data: modelsData } = useModels();
  const { data: userToolsData } = useTools('user_created');
  const { data: adminToolsData } = useAdminTools();

  const modelProviders = useMemo(() => parseModelProviders(modelsData), [modelsData]);
  const providerOptions = useMemo(
    () => modelProviders.map(p => ({ id: p.id, label: p.label })),
    [modelProviders],
  );
  const modelOptions = useMemo(
    () => modelProviders.find(p => p.id === provider)?.models || [],
    [modelProviders, provider],
  );
  const toolOptions = useMemo(() => {
    const userTools = (userToolsData?.tools || []).map(t => ({ id: t.tool_id, label: t.tool_name }));
    const adminTools = (adminToolsData?.tools || []).map(t => ({
      id: t.tool_id,
      label: t.tool_name,
      tag: 'Admin',
    }));
    return [...userTools, ...adminTools];
  }, [userToolsData, adminToolsData]);

  const defaultModelId = modelProviders[0]?.models?.[0]?.id || null;

  // If models finish loading after a team was opened for edit, backfill the provider select.
  useEffect(() => {
    if (!provider && modelId && modelProviders.length) {
      setProvider(findProviderForModel(modelProviders, modelId) || modelProviders[0]?.id || '');
    }
  }, [provider, modelId, modelProviders]);

  const startNew = () => {
    if (teams.length >= user?.plan?.team_creation_limit) {
      Toast({ type: 'info', message: 'Team limit exceed' });
      return;
    }
    setActiveTeam('new');
    setName('');
    setDescription('');
    setIconColor(accentColors[0]);
    setIconDataUrl(null);
    setSelectedAgents([]);
    setMemberRoles({});
    setSelectedTools([]);
    setPattern('sequential');
    setRules('');
    setProvider(modelProviders[0]?.id || '');
    setModelId(defaultModelId);
  };

  const editTeam = async (t, index = 0) => {
    setActiveTeam(t);
    setName(t.team_name || '');
    setDescription(t.description || '');
    setIconColor(getTeamAccent(t, index));
    setIconDataUrl(resolveIconSrc(t.icon));
    setSelectedAgents([]);
    setMemberRoles({});
    setSelectedTools([]);
    setPattern('sequential');
    setRules('');
    setLoadingDetail(true);
    try {
      const resp = await teamService.getTeamDetails(t.team_id);
      const detail = resp?.detail || resp;
      setName(detail?.team_name || t.team_name || '');
      setDescription(detail?.description || '');
      setRules(detail?.instructions || '');
      setPattern(flagsToPattern(detail));
      const resolvedModelId = detail?.model_id || defaultModelId;
      setModelId(resolvedModelId);
      setProvider(findProviderForModel(modelProviders, resolvedModelId) || modelProviders[0]?.id || '');
      setSelectedTools(Array.isArray(detail?.tools) ? detail.tools.filter(Boolean) : []);
      const members = detail?.members || [];
      setSelectedAgents(members.map(m => m.agent_id).filter(Boolean));
      setMemberRoles(
        members.reduce((acc, m) => {
          if (m.agent_id) acc[m.agent_id] = m.agent_role || '';
          return acc;
        }, {}),
      );
      setIconDataUrl(resolveIconSrc(detail?.icon) || resolveIconSrc(t.icon));
      setActiveTeam({ ...t, ...detail, id: t.team_id });
    } catch (error) {
      console.log(error);
      Toast({ type: 'error', message: error?.detail || error?.message || 'Failed to load team' });
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleAgent = id => {
    setSelectedAgents(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const toggleTool = id => {
    setSelectedTools(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleGeneratePrompt = async () => {
    if (!promptText.trim()) return;
    try {
      setPromptLoading(true);
      const resp = await agentService.generatePrompt(promptText.trim());
      if (resp?.success) {
        setRules(resp.prompt.replace(/\\n/g, '\n'));
        Toast({ type: 'success', message: 'Prompt Generated Successfully' });
        setPromptOpen(false);
        setPromptText('');
      }
    } catch (error) {
      console.log(error);
      Toast({ type: 'error', message: error?.detail || error?.message || 'Could not generate prompt' });
    } finally {
      setPromptLoading(false);
    }
  };

  const handleIconUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      Toast({ type: 'error', message: 'Icon must be under 5MB' });
      return;
    }
    try {
      // SVG uploads break once S3 re-serves them as binary/octet-stream — convert to PNG first.
      if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        setIconDataUrl(await svgToPngDataUrl(await file.text()));
      } else {
        setIconDataUrl(await fileToDataUrl(file));
      }
    } catch (error) {
      console.log(error);
      Toast({ type: 'error', message: 'Could not read the selected image' });
    } finally {
      e.target.value = '';
    }
  };

  // Uploaded image wins; unchanged existing http(s) icon is sent back as-is;
  // otherwise fall back to the predefined team glyph in the chosen color.
  const buildIconPayload = async () => {
    if (iconDataUrl?.startsWith('data:')) return iconDataUrl;
    if (iconDataUrl && (activeTeam?.icon || '').startsWith('http')) return activeTeam.icon;
    return createTeamIconDataUrl(iconColor);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    const resolvedModel = modelId || defaultModelId;
    if (!resolvedModel) {
      Toast({ type: 'error', message: 'No model available. Please try again.' });
      return;
    }

    const flags = patternToFlags(pattern);
    const members = selectedAgents
      .map(agentId => {
        const agent = agents.find(a => a.id === agentId);
        if (!agent) return null;
        return {
          agent_id: agent.id,
          agent_name: agent.name,
          agent_role: memberRoles[agentId]?.trim() || patterns.find(p => p.id === pattern)?.label || 'Member',
        };
      })
      .filter(Boolean);

    const payload = {
      team_name: name.trim(),
      model_id: resolvedModel,
      platform_id: process.env.REACT_APP_PLATFORM_ID || 'agentcy',
      description: description.trim(),
      instructions: rules.trim() || description.trim() || name.trim(),
      respond_directly: flags.respond_directly,
      delegate_ask_to_all_members: flags.delegate_ask_to_all_members,
      determine_input_for_members: flags.determine_input_for_members,
      icon: await buildIconPayload(),
      tools: selectedTools,
      members,
    };

    try {
      if (activeTeam === 'new') {
        await createTeam.mutateAsync(payload);
        Toast({ type: 'success', message: 'Team created successfully' });
      } else {
        await updateTeam.mutateAsync({
          ...payload,
          team_id: activeTeam?.team_id || activeTeam?.id,
        });
        Toast({ type: 'success', message: 'Team updated successfully' });
      }
      setActiveTeam(null);
    } catch (err) {
      Toast({ type: 'error', message: err?.detail || err?.message || 'Could not save team' });
    }
  };

  const filteredAgents = agents.filter(a => !query || a.name?.toLowerCase().includes(query.toLowerCase()));
  const saving = createTeam.isPending || updateTeam.isPending;

  return (
    <div className="p-8 lg:p-10">
      {promptOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPromptOpen(false)}>
          <div
            className="w-full max-w-lg rounded-xl border border-shell-border bg-white p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-ink">Generate team instructions</h3>
              <button
                type="button"
                onClick={() => setPromptOpen(false)}
                className="rounded-md p-1.5 text-mute hover:bg-paper hover:text-ink"
                aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={promptText}
              onChange={e => setPromptText(e.target.value)}
              rows={5}
              placeholder="Enter your prompt here."
              className={`${inputClass} resize-y mb-4`}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPromptOpen(false)}
                className="px-4 py-2 text-sm text-mute hover:text-ink">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGeneratePrompt}
                disabled={promptLoading || !promptText.trim()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50">
                {promptLoading ? 'Generating...' : 'Generate prompt'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-ink mb-2 tracking-tight">Team</h1>
          <p className="text-mute">
            Create and manage custom AI teams with tailored instructions and diverse skills.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setListTab('mine');
            startNew();
          }}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-medium px-4 py-2 rounded-lg flex-shrink-0">
          <Plus className="w-4 h-4" /> New Team
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Left: tabs + list */}
        <div className="space-y-4">
          <div className="flex w-full p-1 rounded-lg bg-paper border border-shell-border">
            <button
              type="button"
              onClick={() => {
                setListTab('mine');
                teamsQuery.refetch();
              }}
              className={cn(
                'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all',
                listTab === 'mine' ? 'bg-white text-ink shadow-soft border border-shell-border' : 'text-mute hover:text-ink',
              )}>
              My Teams
            </button>
            <button
              type="button"
              onClick={() => {
                setListTab('discover');
                setActiveTeam(null);
              }}
              className={cn(
                'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all',
                listTab === 'discover'
                  ? 'bg-white text-ink shadow-soft border border-shell-border'
                  : 'text-mute hover:text-ink',
              )}>
              Discover
            </button>
          </div>

          {listTab === 'discover' ? (
            <DiscoverTeamSection />
          ) : teamsQuery.isPending && !teamsQuery.data ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : teamsQuery.isError && !teams.length ? (
            <div className="text-center text-sm py-12 border border-dashed border-shell-border rounded-xl space-y-3">
              <p className="text-mute">Couldn’t load your teams.</p>
              <button
                type="button"
                onClick={() => teamsQuery.refetch()}
                className="text-primary text-sm font-medium hover:underline">
                Try again
              </button>
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center text-mute text-sm py-12 border border-dashed border-shell-border rounded-xl">
              {teamsQuery.isFetching ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary mx-auto" />
              ) : (
                'No teams yet.'
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {teams.map((t, index) => {
                const accent = getTeamAccent(t, index);
                const isActive =
                  activeTeam !== 'new' && (activeTeam?.team_id === t.team_id || activeTeam?.id === t.team_id);
                const memberCount = t.members?.length || t.agent_count || 0;
                const teamIconSrc = resolveIconSrc(t.icon);
                return (
                  <button
                    key={t.team_id}
                    type="button"
                    onClick={() => editTeam(t, index)}
                    className={`w-full text-left p-4 rounded-xl border transition-colors ${
                      isActive
                        ? 'border-primary bg-paper-2'
                        : 'border-shell-border bg-white hover:border-ink/20'
                    }`}>
                    <div className="flex items-center gap-3 mb-2">
                      {teamIconSrc ? (
                        <img src={teamIconSrc} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${accent}1A` }}>
                          <Users className="w-4 h-4" style={{ color: accent }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-ink truncate">{t.team_name}</p>
                        <p className="text-xs text-mute">{memberCount} agents</p>
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-mute">{flagsToPattern(t)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: editor / empty state */}
        <div className="lg:col-span-2">
          {listTab === 'discover' ? (
            <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center bg-white border border-shell-border rounded-[22px] shadow-soft py-20 px-6">
              <div className="w-14 h-14 rounded-2xl bg-[#F5E6B8]/60 flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-ink" />
              </div>
              <p className="text-base font-semibold text-ink mb-1">Select A Team To Chat</p>
              <p className="text-sm text-mute">Official teams are chat-only. Pick one from Discover to start.</p>
            </div>
          ) : !activeTeam ? (
            <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center bg-white border border-shell-border rounded-[22px] shadow-soft py-20 px-6">
              <div className="w-14 h-14 rounded-2xl bg-[#F5E6B8]/60 flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-ink" />
              </div>
              <p className="text-base font-semibold text-ink mb-1">Select A Team To Edit</p>
              <p className="text-sm text-mute">Choose one from your list or create a new team.</p>
            </div>
          ) : loadingDetail ? (
            <div className="h-full min-h-[420px] flex items-center justify-center bg-white border border-shell-border rounded-[22px] shadow-soft py-20">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="bg-white border border-shell-border rounded-[22px] shadow-soft p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-ink mb-1.5 block">
                    <span className="text-[#ef4444]">*</span> Team Name
                  </label>
                  <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Team name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm text-ink mb-1.5 block">Icon</label>
                  {iconDataUrl ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={iconDataUrl} alt="Team icon" className="w-full h-full object-cover" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setIconDataUrl(null)}
                        className="text-xs text-mute hover:text-[#ef4444]">
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${iconColor}1A` }}>
                        <Users className="w-5 h-5" style={{ color: iconColor }} />
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {accentColors.map(c => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setIconColor(c)}
                            className={`w-6 h-6 rounded-full border-2 ${iconColor === c ? 'border-white' : 'border-transparent'}`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 mt-2 text-mute text-xs hover:text-mute">
                    <Upload className="w-3.5 h-3.5" />
                    {iconDataUrl
                      ? 'Replace image (JPG, PNG, SVG · max 5mb)'
                      : 'Upload custom icon (JPG, PNG, SVG · max 5mb)'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml"
                    className="hidden"
                    onChange={handleIconUpload}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <SimpleSelect
                  label="Provider"
                  value={provider}
                  options={providerOptions}
                  onChange={id => {
                    setProvider(id);
                    const firstModel = modelProviders.find(p => p.id === id)?.models?.[0];
                    setModelId(firstModel?.id || null);
                  }}
                  placeholder="Select any provider to proceed"
                />
                <SimpleSelect
                  label="Response Model"
                  value={modelId}
                  options={modelOptions}
                  onChange={setModelId}
                  placeholder="Select any model to proceed"
                />
              </div>

              <MultiSelect label="Tools" value={selectedTools} onToggle={toggleTool} options={toolOptions} />

              <div>
                <label className="text-sm text-ink mb-1.5 block">Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={10}
                  placeholder="What is this team responsible for?"
                  className={`${inputClass} resize-y min-h-[96px]`}
                />
              </div>

              {/* <div>
                <label className="text-sm text-ink mb-2 block">Interaction Pattern</label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {patterns.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPattern(p.id)}
                      className={`text-left p-3 rounded-lg border text-sm ${
                        pattern === p.id ? 'border-primary bg-primary/5' : 'border-shell-border hover:border-ink/20'
                      }`}>
                      <p className="font-medium text-ink">{p.label}</p>
                      <p className="text-xs text-mute mt-0.5">{p.desc}</p>
                    </button>
                  ))}
                </div>
              </div> */}

              <div>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <label className="text-sm text-ink">Team Members ({selectedAgents.length})</label>
                  <div className="relative w-48">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-mute" />
                    <input
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="Search agents"
                      className="w-full bg-paper border border-shell-border rounded-md pl-8 pr-2 py-1.5 text-xs text-ink placeholder:text-mute outline-none"
                    />
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                  {agentsQuery.isPending ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  ) : filteredAgents.length === 0 ? (
                    <p className="text-sm text-mute py-6 text-center">No agents found.</p>
                  ) : (
                    filteredAgents.map(a => {
                      const selected = selectedAgents.includes(a.id);
                      const agentIconSrc = resolveIconSrc(a.icon);
                      return (
                        <div
                          key={a.id}
                          className={`rounded-lg border ${
                            selected ? 'border-primary/40 bg-primary/5' : 'border-shell-border hover:border-ink/20'
                          }`}>
                          <button
                            type="button"
                            onClick={() => toggleAgent(a.id)}
                            className="w-full flex items-center gap-2 p-2 text-left">
                            {agentIconSrc ? (
                              <img src={agentIconSrc} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
                            ) : (
                              <div
                                className="w-7 h-7 rounded flex items-center justify-center text-[10px] font-bold"
                                style={{
                                  backgroundColor: `${a.icon_color || '#721b1f'}1A`,
                                  color: a.icon_color || '#721b1f',
                                }}>
                                {a.name?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <span className="text-sm text-ink flex-1 truncate">{a.name}</span>
                            {selected && <span className="text-[10px] text-primary">✓ added</span>}
                          </button>
                          {selected && (
                            <div className="px-2 pb-2">
                              <input
                                value={memberRoles[a.id] || ''}
                                onChange={e => setMemberRoles(prev => ({ ...prev, [a.id]: e.target.value }))}
                                placeholder="Assign task"
                                className="w-full bg-paper border border-shell-border rounded-md px-2.5 py-1.5 text-xs text-ink placeholder:text-mute outline-none focus:border-primary/40"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-ink">Instructions</label>
                  <button
                    type="button"
                    onClick={() => setPromptOpen(true)}
                    title="Generate instructions"
                    className="p-1 rounded text-mute hover:text-primary hover:bg-paper">
                    <Sparkles className="w-4 h-4" />
                  </button>
                </div>
                <textarea
                  value={rules}
                  onChange={e => setRules(e.target.value)}
                  rows={14}
                  placeholder="Enter your prompt here. Be creative, be bold, or just be straightforward. We'll handle it either way!"
                  className={`${inputClass} resize-y min-h-[140px]`}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-shell-border">
                <button
                  type="button"
                  onClick={() => setActiveTeam(null)}
                  className="px-4 py-2 text-sm text-mute hover:text-ink">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!name.trim() || saving}
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium bg-primary hover:bg-primary/90 text-white disabled:opacity-50">
                  <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Team'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSection;
